import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { AppRouter } from './router.tsx';
import { useAuthStore } from './store/auth.ts';

const qc = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
});

export function App(): JSX.Element {
  const hydrate = useAuthStore((s) => s.hydrateMe);
  const ssoBootstrap = useAuthStore((s) => s.ssoBootstrap);
  // Boot gate: if there's no local session, try the ambient SSO cookie BEFORE
  // rendering (so a signed-in-elsewhere user never flashes the login page).
  // If a session already exists, don't gate at all.
  const [booting, setBooting] = useState(() => !useAuthStore.getState().accessToken);

  useEffect(() => {
    let active = true;
    void (async () => {
      if (useAuthStore.getState().accessToken) {
        await hydrate();
        return;
      }
      // No local session — attempt ambient SSO, then reveal the app either way.
      await ssoBootstrap();
      if (useAuthStore.getState().accessToken) await hydrate();
      if (active) setBooting(false);
    })();
    return () => {
      active = false;
    };
  }, [hydrate, ssoBootstrap]);

  if (booting) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-sm text-muted-foreground"
        aria-busy="true"
      >
        <span className="animate-pulse">Loading…</span>
      </div>
    );
  }

  return (
    <QueryClientProvider client={qc}>
      <AppRouter />
    </QueryClientProvider>
  );
}
